/**
 * Treatment Record model for tracking therapy sessions
 */
import { v4 as uuidv4 } from 'uuid';
import practitionerService from '../services/PractitionerService';

// 治疗记录状态常量
export const TREATMENT_RECORD_STATUS = {
  DRAFT: 'draft',         // 草稿
  SUBMITTED: 'submitted', // 已提交
  ARCHIVED: 'archived',   // 已归档
  INVALID: 'invalid'      // 作废
};

class TreatmentRecord {
  /**
   * Constructor
   * @param {Object} recordData - Treatment record data
   */
  constructor(recordData = {}) {
    this.id = recordData.id || uuidv4();
    this.customerId = recordData.customerId || '';
    this.practitionerId = recordData.practitionerId || '';
    this.appointmentId = recordData.appointmentId || '';
    this.workOrderId = recordData.workOrderId || '';
    this.treatmentProjectId = recordData.treatmentProjectId || '';
    this.treatmentProjectName = recordData.treatmentProjectName || '';
    this.date = recordData.date || new Date().toISOString();
    // 1. Health Profile & Assessment
    this.healthProfile = recordData.healthProfile || {
      familyHistory: '',
      lifestyle: { diet: '', sleep: '', exercise: '', other: '' },
      height: '',
      weight: '',
      other: ''
    };
    this.tcmAssessment = recordData.tcmAssessment || {
      inspection: { tongueImage: '', faceImage: '', description: '' }, // Inspection (Wang)
      listeningSmelling: { voice: '', smell: '' }, // Listening/Smelling (Wen)
      inquiry: { chiefComplaint: '', symptoms: [], history: '' }, // Inquiry (Wen)
      palpation: { pulse: '', pulseValue: '', other: '' }, // Palpation (Qie)
      constitution: { type: '', score: 0, details: {} }, // Constitution identification
      riskAnalysis: ''
    };
    // 2. Treatment Plan
    this.treatmentPlan = recordData.treatmentPlan || {
      version: 1,
      items: [], // [{ projectId, name, times, materials: [], price }]
      prescriptions: [], // [{ name, dosage, instructions, quantity }]
      dietAdvice: [],
      exerciseAdvice: [],
      lifestyleAdvice: [],
      consent: { signed: false, signedAt: '', fileUrl: '' },
      estimatedCost: 0,
      planNotes: ''
    };
    // 3. Service Execution
    this.serviceExecution = recordData.serviceExecution || {
      schedule: { room: '', startTime: '', endTime: '', operatorId: '' },
      sopChecklist: { alcoholAllergy: false, essentialOilAllergy: false },
      steps: [], // [{ step, startTime, endTime, notes }]
      realTimeFeedback: { painLevel: 0, heatArea: '', parentFeedback: '' },
      aftercare: [],
      serviceNotes: ''
    };
    // 4. Follow Up
    this.followUp = recordData.followUp || [];
    // 5. Settlement
    this.settlement = recordData.settlement || {
      items: [], // [{ name, price, quantity, discount, total }]
      totalAmount: 0,
      paidAmount: 0,
      paymentMethod: '',
      invoiceUrl: ''
    };
    // 6. Attachments
    this.attachments = recordData.attachments || [];
    this.createdAt = recordData.createdAt || new Date().toISOString();
    this.updatedAt = recordData.updatedAt || new Date().toISOString();
    this.status = recordData.status || TREATMENT_RECORD_STATUS.DRAFT;
  }

  /**
   * Update record data
   * @param {Object} recordData - Record data to update
   * @returns {TreatmentRecord} Updated record instance
   */
  update(recordData) {
    Object.keys(recordData).forEach(key => {
      if (recordData[key] !== undefined && key !== 'id') {
        this[key] = recordData[key];
      }
    });
    if (recordData.status !== undefined) {
      this.status = recordData.status;
    }
    this.updatedAt = new Date().toISOString();
    return this;
  }

  /**
   * Convert to JSON for storage
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      customerId: this.customerId,
      practitionerId: this.practitionerId,
      appointmentId: this.appointmentId,
      workOrderId: this.workOrderId,
      treatmentProjectId: this.treatmentProjectId,
      treatmentProjectName: this.treatmentProjectName,
      date: this.date,
      healthProfile: this.healthProfile,
      tcmAssessment: this.tcmAssessment,
      treatmentPlan: this.treatmentPlan,
      serviceExecution: this.serviceExecution,
      followUp: this.followUp,
      settlement: this.settlement,
      attachments: this.attachments,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      status: this.status
    };
  }

  /**
   * Create treatment record from JSON data
   * @param {Object} json - JSON data
   * @returns {TreatmentRecord} TreatmentRecord instance
   * @static
   */
  static fromJSON(json) {
    return new TreatmentRecord(json);
  }

  get practitionerName() {
    if (!this.practitionerId) return '';
    const practitioner = practitionerService.getPractitionerById(this.practitionerId);
    return practitioner ? practitioner.name : '';
  }
}

export default TreatmentRecord;